/**
 * Sound effects for the GuidedOfferCampaignCard
 * Uses Web Audio API to generate sounds programmatically
 */

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      console.warn('Web Audio API not supported');
      return null;
    }
  }

  // Resume context if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }

  return audioContext;
}

/**
 * Play a simple tone
 */
function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3,
  delay: number = 0
): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + delay);

  // Envelope for smoother sound
  gainNode.gain.setValueAtTime(0, ctx.currentTime + delay);
  gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration);

  oscillator.start(ctx.currentTime + delay);
  oscillator.stop(ctx.currentTime + delay + duration);
}

/**
 * Play a frequency sweep
 */
function playSweep(
  startFreq: number,
  endFreq: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3
): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(startFreq, ctx.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(endFreq, ctx.currentTime + duration);

  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

/**
 * Game card selection - satisfying click with ascending tone
 */
export function playSelectSound(): void {
  // Quick satisfying click
  playTone(800, 0.08, 'square', 0.15);
  // Ascending confirmation tone
  playTone(600, 0.1, 'sine', 0.2, 0.05);
  playTone(900, 0.15, 'sine', 0.25, 0.1);
}

/**
 * Hover sound - subtle tick
 */
export function playHoverSound(): void {
  playTone(1200, 0.03, 'sine', 0.08);
}

/**
 * Loading progress tick - subtle advancement sound
 */
export function playProgressTick(): void {
  playTone(440 + Math.random() * 200, 0.05, 'sine', 0.1);
}

/**
 * Loading complete - triumphant ascending sweep
 */
export function playLoadingComplete(): void {
  playSweep(400, 800, 0.2, 'sine', 0.25);
  playTone(800, 0.15, 'sine', 0.2, 0.15);
  playTone(1000, 0.2, 'sine', 0.25, 0.25);
}

/**
 * Success celebration - victory fanfare
 */
export function playSuccessSound(): void {
  // Triumphant chord progression
  playTone(523, 0.3, 'sine', 0.2); // C5
  playTone(659, 0.3, 'sine', 0.2, 0.05); // E5
  playTone(784, 0.35, 'sine', 0.25, 0.1); // G5
  playTone(1047, 0.4, 'sine', 0.3, 0.2); // C6

  // Sparkle effect
  for (let i = 0; i < 5; i++) {
    playTone(1500 + i * 200, 0.08, 'sine', 0.1, 0.3 + i * 0.05);
  }
}

/**
 * Copy button click - confirmation beep
 */
export function playCopySound(): void {
  playTone(880, 0.08, 'square', 0.15);
  playTone(1100, 0.12, 'sine', 0.2, 0.06);
}

/**
 * Countdown warning beep (10 seconds remaining)
 */
export function playWarningBeep(): void {
  playTone(600, 0.15, 'sine', 0.2);
  playTone(600, 0.15, 'sine', 0.2, 0.2);
}

/**
 * Countdown critical beep (5 seconds remaining) - faster, more urgent
 */
export function playCriticalBeep(): void {
  playTone(800, 0.1, 'square', 0.25);
}

/**
 * Countdown tick - subtle urgency builder
 */
export function playCountdownTick(): void {
  playTone(1000, 0.03, 'sine', 0.12);
}

/**
 * Final countdown - dramatic descending tone
 */
export function playFinalCountdown(): void {
  playSweep(1200, 400, 0.5, 'sawtooth', 0.2);
}

/**
 * Return/reset sound - soft descending
 */
export function playReturnSound(): void {
  playSweep(600, 300, 0.15, 'sine', 0.15);
}

/**
 * Screen transition whoosh
 */
export function playTransitionSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Create noise for whoosh effect
  const bufferSize = ctx.sampleRate * 0.15;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = buffer.getChannelData(0);

  for (let i = 0; i < bufferSize; i++) {
    output[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }

  const noise = ctx.createBufferSource();
  noise.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1000, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(3000, ctx.currentTime + 0.1);
  filter.Q.value = 1;

  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);

  noise.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(ctx.destination);

  noise.start(ctx.currentTime);
}

/**
 * Urgency pulse - played during critical countdown
 */
export function playUrgencyPulse(): void {
  playTone(200, 0.1, 'sine', 0.15);
  playTone(250, 0.1, 'sine', 0.12, 0.1);
}
